/**
 * MultiFilePreview — Horizontal filmstrip of uploaded files
 * Used in Practice Film for multi-file media bundles.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X as CloseIcon, Film, FileText, Music, Image, Link2, Plus, Camera } from 'lucide-react';
import { C } from '../design-system/primitives';

const FILE_TYPE_MAP = {
  'video/mp4': { icon: Film, label: 'Video', color: '#22c55e' },
  'video/quicktime': { icon: Film, label: 'Video', color: '#22c55e' },
  'video/webm': { icon: Film, label: 'Video', color: '#22c55e' },
  'audio/mpeg': { icon: Music, label: 'Audio', color: '#8b5cf6' },
  'audio/wav': { icon: Music, label: 'Audio', color: '#8b5cf6' },
  'audio/webm': { icon: Music, label: 'Audio', color: '#8b5cf6' },
  'application/pdf': { icon: FileText, label: 'PDF', color: '#ef4444' },
  'text/csv': { icon: FileText, label: 'CSV', color: '#0ea5e9' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileText, label: 'Doc', color: '#3b82f6' },
  'application/msword': { icon: FileText, label: 'Doc', color: '#3b82f6' },
  'image/jpeg': { icon: Image, label: 'Image', color: '#f59e0b' },
  'image/png': { icon: Image, label: 'Image', color: '#f59e0b' },
  'image/webp': { icon: Image, label: 'Image', color: '#f59e0b' },
};

function getFileInfo(file) {
  return FILE_TYPE_MAP[file.type] || { icon: FileText, label: 'File', color: '#71717a' };
}

function VideoThumbnail({ file }) {
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(file);
    video.src = url;
    video.onloadeddata = () => {
      video.currentTime = 0.5;
    };
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 80;
      canvas.height = 45;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, 80, 45);
      setThumb(canvas.toDataURL('image/jpeg', 0.6));
      URL.revokeObjectURL(url);
    };
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return thumb ? (
    <img src={thumb} alt="Video thumbnail" className="w-full h-full object-cover rounded" />
  ) : (
    <div className="w-full h-full bg-zinc-800 rounded flex items-center justify-center">
      <Film className="w-5 h-5 text-green-400/60" />
    </div>
  );
}

export default function MultiFilePreview({ files, urls, onRemoveFile, onRemoveUrl, onAddUrl, onAddFiles, onOpenCamera }) {
  const scrollRef = useRef(null);
  const extraInputRef = useRef(null);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);

  const handleAddUrl = () => {
    const trimmed = urlInput.trim();
    if (trimmed && (trimmed.startsWith('http') || trimmed.includes('youtube.com') || trimmed.includes('youtu.be'))) {
      onAddUrl(trimmed);
      setUrlInput('');
      setShowUrlInput(false);
    }
  };

  const totalCount = files.length + urls.length;
  if (totalCount === 0) return null;

  return (
    <div className="flex-shrink-0 border-t border-zinc-800 bg-zinc-900/80 px-2 py-1.5">
      <div className="flex items-center gap-1.5 mb-1">
        <span style={{ fontSize: 9, fontWeight: 700, color: C.gold, letterSpacing: 1.5, textTransform: 'uppercase' }}>
          Media Bundle
        </span>
        <span className="text-zinc-500 text-[10px]">({totalCount} file{totalCount !== 1 ? 's' : ''})</span>
      </div>

      <div ref={scrollRef} className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
        {/* File cards */}
        {files.map((f, i) => {
          const info = getFileInfo(f);
          const Icon = info.icon;
          const isVideo = f.type.startsWith('video/');
          const isImage = f.type.startsWith('image/');

          return (
            <div key={`file-${i}`} className="flex-shrink-0 relative group" style={{ width: 80, height: 56 }}>
              {isVideo ? (
                <VideoThumbnail file={f} />
              ) : isImage ? (
                <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover rounded" />
              ) : (
                <div className="w-full h-full bg-zinc-800 rounded flex flex-col items-center justify-center gap-0.5">
                  <Icon className="w-4 h-4" style={{ color: info.color }} />
                  <span className="text-[8px] text-zinc-400 truncate max-w-[70px] px-1">{info.label}</span>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5 rounded-b">
                <span className="text-[7px] text-zinc-300 truncate block">{f.name}</span>
              </div>
              <button
                onClick={() => onRemoveFile(i)}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <CloseIcon className="w-2.5 h-2.5 text-white" />
              </button>
            </div>
          );
        })}

        {/* URL cards */}
        {urls.map((url, i) => {
          const isYoutube = url.includes('youtube.com') || url.includes('youtu.be');
          return (
            <div key={`url-${i}`} className="flex-shrink-0 relative group" style={{ width: 80, height: 56 }}>
              <div className="w-full h-full bg-zinc-800 rounded flex flex-col items-center justify-center gap-0.5">
                <Link2 className="w-4 h-4 text-blue-400" />
                <span className="text-[8px] text-zinc-400">{isYoutube ? 'YouTube' : 'URL'}</span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5 rounded-b">
                <span className="text-[7px] text-zinc-300 truncate block">{new URL(url).hostname}</span>
              </div>
              <button
                onClick={() => onRemoveUrl(i)}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <CloseIcon className="w-2.5 h-2.5 text-white" />
              </button>
            </div>
          );
        })}

        {/* Add more buttons */}
        <div className="flex-shrink-0 flex gap-1">
          {onOpenCamera && (
            <button
              onClick={onOpenCamera}
              className="w-10 h-[56px] bg-zinc-800 hover:bg-zinc-700 rounded flex flex-col items-center justify-center gap-0.5 transition-colors"
              title="Take photo/video"
            >
              <Camera className="w-3.5 h-3.5 text-green-400" />
              <span className="text-[7px] text-zinc-500">Cam</span>
            </button>
          )}
          <button
            onClick={() => extraInputRef.current?.click()}
            className="w-10 h-[56px] bg-zinc-800 hover:bg-zinc-700 rounded flex flex-col items-center justify-center gap-0.5 transition-colors"
            title="Add files"
          >
            <Plus className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[7px] text-zinc-500">File</span>
          </button>
          <button
            onClick={() => setShowUrlInput(!showUrlInput)}
            className="w-10 h-[56px] bg-zinc-800 hover:bg-zinc-700 rounded flex flex-col items-center justify-center gap-0.5 transition-colors"
            title="Add URL"
          >
            <Link2 className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-[7px] text-zinc-500">URL</span>
          </button>
        </div>

        <input
          ref={extraInputRef}
          type="file"
          multiple
          accept="video/*,audio/*,.csv,.pdf,.doc,.docx,image/*"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) {
              onAddFiles(Array.from(e.target.files));
              e.target.value = '';
            }
          }}
        />
      </div>

      {/* URL input row */}
      {showUrlInput && (
        <div className="flex gap-1 mt-1">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddUrl()}
            placeholder="YouTube or web URL..."
            className="flex-1 min-w-0 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-white placeholder-zinc-600 focus:outline-none focus:border-blue-400"
            autoFocus
          />
          <button
            onClick={handleAddUrl}
            className="px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-bold rounded"
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}
